import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  Announcement,
  ActiveAnnouncement,
  AnnouncementStats,
  PaginatedAnnouncements,
  CreateAnnouncementDto,
  UpdateAnnouncementDto,
  AnnouncementQueryParams,
  AnnouncementPlatform,
} from '../types';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch paginated list of announcements for admin management.
 */
async function getAnnouncements(params?: AnnouncementQueryParams): Promise<PaginatedAnnouncements> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const url = `/admin/announcements${queryString ? `?${queryString}` : ''}`;

  return apiFetch<PaginatedAnnouncements>(url);
}

/**
 * Fetch a single announcement by ID.
 */
async function getAnnouncement(id: string): Promise<Announcement> {
  return apiFetch<Announcement>(`/admin/announcements/${id}`);
}

/**
 * Fetch dismissal statistics for an announcement.
 */
async function getAnnouncementStats(id: string): Promise<AnnouncementStats> {
  return apiFetch<AnnouncementStats>(`/admin/announcements/${id}/stats`);
}

/**
 * Create a new announcement.
 */
async function createAnnouncement(data: CreateAnnouncementDto): Promise<Announcement> {
  return apiFetch<Announcement>('/admin/announcements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing announcement.
 */
async function updateAnnouncement(id: string, data: UpdateAnnouncementDto): Promise<Announcement> {
  return apiFetch<Announcement>(`/admin/announcements/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete an announcement.
 */
async function deleteAnnouncement(id: string): Promise<void> {
  await apiFetch(`/admin/announcements/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Fetch active announcements for the current user.
 */
async function getActiveAnnouncements(
  platform: AnnouncementPlatform = 'web'
): Promise<ActiveAnnouncement[]> {
  return apiFetch<ActiveAnnouncement[]>(`/system/announcements/active?platform=${platform}`);
}

/**
 * Dismiss an announcement for the current user.
 */
async function dismissAnnouncement(id: string): Promise<void> {
  await apiFetch(`/system/announcements/${id}/dismiss`, {
    method: 'POST',
  });
}

// ============================================================================
// Admin Hooks
// ============================================================================

/**
 * Hook to fetch paginated list of announcements for admin management.
 * Supports filtering by status and severity, and pagination.
 */
export function useAnnouncements(params?: AnnouncementQueryParams) {
  return useQuery({
    queryKey: queryKeys.announcements.list(params),
    queryFn: () => getAnnouncements(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch a single announcement by ID.
 */
export function useAnnouncement(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.announcements.detail(id),
    queryFn: () => getAnnouncement(id),
    enabled: options?.enabled ?? !!id,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch dismissal statistics for an announcement.
 */
export function useAnnouncementStats(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.announcements.stats(id),
    queryFn: () => getAnnouncementStats(id),
    enabled: options?.enabled ?? !!id,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to create a new announcement.
 * Invalidates announcement list on success.
 */
export function useCreateAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => {
      // Invalidate announcement list
      queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
    },
  });
}

/**
 * Hook to update an existing announcement.
 * Updates cache and invalidates list on success.
 */
export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAnnouncementDto }) =>
      updateAnnouncement(id, data),
    onSuccess: (updatedAnnouncement, { id }) => {
      // Update the detail cache
      queryClient.setQueryData(queryKeys.announcements.detail(id), updatedAnnouncement);

      // Invalidate announcement list to reflect changes
      queryClient.invalidateQueries({
        queryKey: queryKeys.announcements.list(),
      });

      // Invalidate active announcements (may have changed status)
      queryClient.invalidateQueries({
        queryKey: queryKeys.announcements.active(),
      });
    },
  });
}

/**
 * Hook to delete an announcement.
 * Invalidates list and removes from cache on success.
 */
export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: (_, deletedId) => {
      // Remove from detail cache
      queryClient.removeQueries({
        queryKey: queryKeys.announcements.detail(deletedId),
      });

      // Invalidate announcement list
      queryClient.invalidateQueries({
        queryKey: queryKeys.announcements.list(),
      });

      // Invalidate active announcements
      queryClient.invalidateQueries({
        queryKey: queryKeys.announcements.active(),
      });
    },
  });
}

// ============================================================================
// Public/User Hooks
// ============================================================================

/**
 * Hook to fetch active announcements for the current user.
 * Works for both authenticated and anonymous users.
 */
export function useActiveAnnouncements(platform: AnnouncementPlatform = 'web') {
  return useQuery({
    queryKey: queryKeys.announcements.active(platform),
    queryFn: () => getActiveAnnouncements(platform),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });
}

/**
 * Hook to dismiss an announcement for the current user.
 * Removes from active announcements cache on success.
 */
export function useDismissAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dismissAnnouncement,
    onSuccess: (_, dismissedId) => {
      // Optimistically remove from active announcements cache
      queryClient.setQueriesData<ActiveAnnouncement[]>(
        { queryKey: queryKeys.announcements.active() },
        (old) => old?.filter((a) => a.id !== dismissedId)
      );

      // Invalidate stats for this announcement
      queryClient.invalidateQueries({
        queryKey: queryKeys.announcements.stats(dismissedId),
      });
    },
  });
}
